<template>
    <form ref="form" v-on="formEvents">
        <div class="form-group">
            <label>Account Number</label>
            <div class="input-group">
                <div class="input-group-prepend">
                        <span
                            :class="['input-group-text' , (meter.name && !meterLoading) ? 'bg-primary text-white' : '']">
                               <i v-if="meterLoading" class="fas fa-spinner fa-pulse"></i>
                               <i v-if="meter.name && !meterLoading" class="fas fa-check"></i>
                        </span>
                </div>
                <input required
                       @keyup="showForm"
                       v-model="form.account" type="number"
                       :class="[ 'form-control' , form.errors.get('account') ? 'is-invalid' : '' ]"
                       placeholder="Enter your Telone Account number">
                <div v-text="form.errors.get('account')" class="invalid-feedback mb-2"/>
            </div>
        </div>
        <div class="form-group">
            <button
                type="button"
                :class="['btn shadow-none btn-block btn-sm mt-2', form.account ? 'btn-primary' : 'bg-dark-5 text-white']"
                :disabled="meterLoading || !form.account"
                @click="checkMeterNumber">
                <i v-if="meterLoading" class="fas fa-spinner fa-pulse mr-1"></i>
                Verify
            </button>
        </div>

        <transition name="slide-in-top">
            <div v-if="meter.name && !meterLoading">
                <p class="text-1 text-center p-1 bg-primary text-white">
                    {{ meter.name }} - {{ meter.address }}
                </p>

                <div v-if="loading"
                     class="text-center text-primary text-20 line-height-07 mb-5 mt-5 d-flex justify-content-center align-items-center">
                    <div class="loader"></div>
                </div>

                <div v-if="!loading " class="form-group">
                    <label>Category</label>
                    <select required v-model="form.category" @change="onCategoryChange"
                            :class="[ 'custom-select' , form.errors.get('category') ? 'is-invalid' : '' ]">
                        <option value="">Choose Category</option>
                        <option v-for="cat in categories" :value="cat">{{ formatCategoryLabel(cat) }}</option>
                    </select>
                    <div v-text="form.errors.get('category')" class="invalid-feedback"/>
                </div>


                <div v-if="!loading" class="form-group">
                    <label>Bundle</label>
                    <select required v-model="form.bundle" :disabled ="!form.category"
                            :class="[ 'custom-select' , form.errors.get('bundle') ? 'is-invalid' : '' ]">
                        <option value="">Choose Bundle</option>
                        <option v-for="b in filteredBundles" :value="b.id">
                            {{ formatBundleOptionLabel(b) }}
                        </option>
                    </select>
                    <div v-text="form.errors.get('bundle')" class="invalid-feedback"/>
                </div>

                <div v-if="selectedBundle" class="form-group">
                    <label>Selected Bundle Details</label>
                    <div class="border rounded p-3">
                        <div class="d-flex justify-content-between align-items-center mb-2">
                            <strong>{{ selectedBundle.name }}</strong>
                            <span class="badge badge-primary bundle-price-badge">{{ form.currency }}{{ selectedBundle.price }}</span>
                        </div>
                        <div class="d-flex flex-wrap">
                            <span
                                v-for="(item, index) in selectedBundleDescriptionItems"
                                :key="`bundle-detail-${index}`"
                                :class="['mr-2 mb-2 bundle-detail-chip', item.chipClass]"
                            >
                                <i :class="[item.iconClass, 'mr-1']"></i>
                                {{ item.label }}
                            </span>
                        </div>
                    </div>
                </div>

                <div class="form-group">
                    <label>SMS Notification Number </label>
                    <phone-input v-model="form.phone"></phone-input>
                    <div v-text="form.errors.get('phone')" class="text-danger"/>
                    <small>* Used to deliver your Recharge PIN</small>
                </div>

                <div class="form-group">
                    <label>Pay with</label>
                    <select v-model="form.payment"
                            :class="[ 'custom-select' , form.errors.get('payment') ? 'is-invalid' : '' ]">
                        <option v-if="isEcocashZwgEnabled && form.currency === 'ZWG'" value="ecocash">EcoCash ZWG</option>
                        <option v-if="isEcocashUsdEnabled" value="ecocash-usd">EcoCash USD</option>
                        <option v-if="isStripeEnabled" value="stripe">International Card</option>
                    </select>
                    <div v-text="form.errors.get('payment')" class="invalid-feedback"/>
                </div>

                <div v-if="form.currency === 'USD' && form.payment === 'stripe'" class="alert alert-warning">
                    We have added a {{ paymentPremium }}% fee to recover the costs of card processing.
                    This currently applies only to this product. You will therefore be billed ${{ totalPayment }} at checkout.
                    <div class="custom-control custom-checkbox">
                        <input type="checkbox" class="custom-control-input" id="terms" required v-model="paymentPremiumAccepted">
                        <label class="custom-control-label font-weight-bold" for="terms">
                            I accept these terms.
                        </label>
                    </div>
                </div>

                <div v-if="form.payment === 'ecocash' || form.payment === 'ecocash-usd'" class="form-group">
                    <label>EcoCash Phone Number</label>
                    <phone-input v-model="form.ecocash" :countries="['ZW']"></phone-input>
                    <div v-text="form.errors.get('ecocash')" class="text-danger"/>
                </div>

                <div class="form-group">
                    <label>Email</label>
                    <input type="email" v-model="form.email" value="email"
                           required
                           :class="[ 'form-control' , form.errors.get('email') ? 'is-invalid' : '' ]"
                           placeholder="Email">
                    <div v-text="form.errors.get('email')" class="invalid-feedback"/>
                </div>

                <div  class="form-group" v-if="form.payment">
                    <label>Amount <br>
                        {{ activeDiscountPercent > 0 ? `[ Promotional discount applied: ${activeDiscountPercent}% ]` : '' }}
                    </label>
                    <div class="input-group">
                        <div class="input-group-prepend">
                    <span class="input-group-text">
                        {{ !methods[form.payment] ? form.currency : methods[form.payment] }}
                    </span>
                        </div>
                        <input type="number" disabled :value="totalPayment"
                               :class="[ 'form-control' , form.errors.get('amount') ? 'is-invalid' : '' ]"
                               placeholder="Amount">
                        <!-- <div v-if="form.payment === 'stripe'" class="input-group-prepend bg-success text-white">
                             <span class="input-group-text bg-success text-white">
                                 Discounted Price
                             </span>
                         </div>-->
                        <div v-text="form.errors.get('amount')" class="invalid-feedback"/>
                    </div>
                </div>
                <div class="form-group d-flex align-items-center">
                    <input type="checkbox" class="mr-2" id="checkterms" v-model="termsAccepted">
                    <label class="mb-0" for="checkterms">I accept the <a target="_blank" href="/terms">Terms & Conditions</a></label>
                </div>
            </div>
        </transition>

        <div class="form-group">
            <button @click="formVisibility ? submit($event) : (formVisibility = true)"
                    type="button"
                    v-bind="buttonValidity"
                    :class="{'btn-loading': form.loading, ...buttonValidity.class }">Buy Bundle
            </button>
        </div>
    </form>
</template>

<script>
import Form from "../../../core/forms/form";
import PhoneInput from "../../custom/phone-input.vue";
import {validity} from "../../../core/forms/validity";
import {appDataMixin, paymentMethods, systems} from "../../../core/data/global";

export default {
    name: "dashboard",
    mixins: [validity, appDataMixin],
    components: {PhoneInput},
    data: function () {
        return {
            meterLoading: false,
            fields: [
                'account',
                'phone',
                'amount',
                'email',
                'ecocash',
                'currency',
                'bundle_price',
                'bundle_currency'
            ],
            methods: {
                'ecocash': 'ZWG',
                'ecocash-usd': 'USD',
                'stripe': 'USD',
            },
            meter: {},
            bundles: [],
            bundlesUSD: [],
            categories: [],
            rate: 1,
            paymentPremium:0,
            paymentPremiumAccepted: false,
            discountUsd: 0,
            discountZwg: 0,
            loading: false,
            form: new Form({
                'type': 'recharge',
                'account': '',
                'payment': 'ecocash-usd',
                'category': '',
                'bundle': '',
                'currency': '',
                'phone': '',
                'ecocash': '',
                'amount': '',
                'email': '',
                'bundle_price': '',
                'bundle_currency': '',
            })
        };
    },

    computed: {
        totalPayment(){
            const amount = parseFloat(this.discounted) || 0;
            if(this.form.payment === 'stripe' && this.form.currency === 'USD'){
                const premium = 1 + (this.paymentPremium / 100);
                return (amount * premium).toFixed(2);
            }
            return amount.toFixed(2);
        },
        activeDiscountPercent() {
            return this.form.currency === 'USD' ? this.discountUsd : this.discountZwg;
        },
        price: function () {
            return this.calculateUsdPrice(this.selected_bundle_price);
        },
        discounted: function () {
            return this.calculateDiscountedUsdPrice(this.selected_bundle_price);
        },
        selected_bundle_price() {
            return this.selectedBundle ? parseFloat(this.selectedBundle.price) : 0;
        },
        selectedBundle() {
            if (this.form.bundle === '' || this.loading) {
                return null;
            }

            return this.bundles.find(e => String(e.code) === String(this.form.bundle)) || null;
        },
        selectedBundleDescriptionItems() {
            if (!this.selectedBundle || !this.selectedBundle.description) {
                return [];
            }

            return this.selectedBundle.description
                .split('+')
                .map((item) => item.trim())
                .filter(Boolean)
                .flatMap((item) => this.expandDescriptionItem(item))
                .map((item) => this.getDetailChipMeta(item));
        },
        filteredBundles() {
            
            const filtered = this.form.category === '' && !this.loading 
                ? this.bundles 
                : this.bundles.filter(e => e.category === this.form.category);
                
            return filtered.map(e => ({
                ...e,
                id: e.code,
                price: parseFloat(e.price || 0),
                ratedPrice: this.methods[this.form.payment] === "ZWG" ? e.price : this.calculateUsdPrice(e.price)
            }));
        },
        isEcocashZwgEnabled() {
            return systems.ecocash_payment && systems.ecocash_zwg_payment;
        },
        isEcocashUsdEnabled() {
            return systems.ecocash_payment && systems.ecocash_usd_payment;
        },
        isEcocashEnabled() {
            return this.isEcocashZwgEnabled || this.isEcocashUsdEnabled;
        },
        isStripeEnabled() {
            return paymentMethods.includes('stripe');
        }
    },

    methods: {

        calculateUsdPrice(price) {
            price = parseFloat(price);
            return this.methods[this.form.payment] === "ZWG"
                ? price :
                ((price / this.rate)).toFixed(2);
        },

        calculateDiscountedUsdPrice(price) {
            price = parseFloat(price);
            const multiplier = 1 - (this.activeDiscountPercent / 100);
            return (price * (multiplier < 0 ? 0 : multiplier)).toFixed(2);
        },

        expandDescriptionItem(item) {
            return item
                .split(/(?=(?:Voice\s+Onnet|Voice\s+Offnet|Onnet|Offnet|Netflix|Youtube))/i)
                .map((part) => part.trim())
                .filter(Boolean);
        },

        getDetailChipMeta(item) {
            const value = item.toLowerCase();

            if (value.includes('netflix')) {
                return {
                    label: item,
                    iconClass: 'fas fa-tv text-danger',
                    chipClass: 'bundle-chip-danger'
                };
            }

            if (value.includes('youtube')) {
                return {
                    label: item,
                    iconClass: 'fas fa-video text-danger',
                    chipClass: 'bundle-chip-danger'
                };
            }

            if (value.includes('onnet') || value.includes('offnet') || value.includes('voice') || value.includes('minute')) {
                return {
                    label: item,
                    iconClass: 'fas fa-phone text-purple',
                    chipClass: 'bundle-chip-purple'
                };
            }

            if (value.includes('mbps') || value.includes('speed')) {
                return {
                    label: item,
                    iconClass: 'fas fa-bolt text-success',
                    chipClass: 'bundle-chip-success'
                };
            }

            if (value.includes('gb') || value.includes('unlimited') || value.includes('fup') || value.includes('data')) {
                return {
                    label: item,
                    iconClass: 'fas fa-database text-success',
                    chipClass: 'bundle-chip-success'
                };
            }

            return {
                label: item,
                iconClass: 'fas fa-info-circle text-muted',
                chipClass: 'bundle-chip-default'
            };
        },

        onCategoryChange() {
            this.form.bundle = '';
            this.form.bundle_price = '';
            this.form.errors.clear('bundle');
        },

        formatCategoryLabel(category) {
            const normalized = String(category || '').replace(/_/g, ' ').trim();
            if (!normalized) {
                return '';
            }
            return normalized.charAt(0).toUpperCase() + normalized.slice(1);
        },

        formatBundleOptionLabel(b) {
            if (b.category === 'voice') {
                return b.name;
            }
            return `${b.name} - ${this.form.currency}${b.price}`;
        },

        checkMeterNumber() {
            if (this.form.account === '') {
                return;
            }

            this.meterLoading = true;
            this.meter = {};
            
            // Clear any previous account errors
            this.form.errors.clear('account');
            
            let form = new Form({
                account: this.form.account
            });

            form.submit('/telone/check').then((response) => {
                this.meter = response.data.body.meter;
                this.fetchBundlesForAccount();
            }).catch((error) => {
                this.meter = {};
                this.bundles = [];
                this.categories = [];
                
                // Transfer validation errors from the temporary form to the main form
                if (form.errors.has('account')) {
                    this.form.errors.record({ account: [form.errors.get('account')] });
                }
                console.log(this.form.errors.get('account'));
            }).finally(() => {
                this.meterLoading = false;
            });
        },

        fetchBundlesForAccount() {
            if (!this.form.account || !this.form.currency) {
                return;
            }

            this.loading = true;
            
            let form = new Form({
                account: this.form.account,
                currency: this.form.currency
            });

            form.submit('/telone/bundles').then((response) => {
                
                this.bundles = response.data.body.bundles || [];
                this.categories = response.data.body.categories || [];
                this.form.category = '';
                this.form.bundle = '';
                
                this.rate = response.data.body.rate || this.rate;
                this.paymentPremium = response.data.body.premium || this.paymentPremium;
                this.discountUsd = response.data.body.discount_usd || 0;
                this.discountZwg = response.data.body.discount_zwg || 0;
                
            }).catch((error) => {
                this.bundles = [];
                this.categories = [];
            }).finally(() => {
                this.loading = false;
            });
        },

        fetchPrepareData() {
            window.axios.get(`${window.location.origin}/telone/prepare`).then((response) => {
                if (this.form.currency === 'USD') {
                    this.paymentPremium = response.data.body.premium;
                }
                this.rate = response.data.body.rate;
                this.discountUsd = response.data.body.discount_usd || response.data.body.discount || 0;
                this.discountZwg = response.data.body.discount_zwg || 0;
            });
        },

        showForm() {},



        submit(event) {

            if (this.$refs.form.checkValidity() && !this.form.errors.any()) {
                event.preventDefault()

                if (this.form.bundle !== '') {
                    const selectedBundle = this.bundles.find(e => String(e.code) === String(this.form.bundle));
                    if (selectedBundle) {
                        this.form.bundle_price = selectedBundle.price || 0;
                        this.form.bundle_currency = this.form.currency;
                        this.form.bundle = selectedBundle.code;
                        this.form.amount = this.totalPayment;
                    }
                }


                this.form.submit('/telone/init').then((response) => {
                    if (response.data.success) {
                        if (response.data.body.payment.payment === "ecocash" || response.data.body.payment.payment === "ecocash-usd") {
                            this.$router.push(`/telone/ecocash?id=${response.data.body.payment.ref}`);
                        }

                        if (response.data.body.payment.payment === "stripe") {
                            window.location.href = response.data.body.payment.url;
                        }

                    } else {

                    }
                });
            }
        }
    },

    mounted() {
        
        this.form.currency = this.currency;
        
        this.fetchPrepareData();


        if (this.form.account !== "") {
            this.checkMeterNumber();
        }

        this.scrollToTop();
    }
}
</script>

<style scoped>
.bundle-price-badge {
    padding: 0.4em 0.65em;
}

.bundle-detail-chip {
    border-radius: 14px;
    border: 1px solid #dee2e6;
    background: #f8f9fa;
    font-size: 0.74rem;
    line-height: 1.1;
    padding: 0.2rem 0.45rem;
}

.bundle-detail-chip i {
    font-size: 0.7rem;
}

.bundle-chip-success {
    color: #1e8e3e;
    border-color: rgba(30, 142, 62, 0.25);
    background: rgba(46, 204, 113, 0.12);
}

.bundle-chip-danger {
    color: #c0392b;
    border-color: rgba(192, 57, 43, 0.25);
    background: rgba(231, 76, 60, 0.1);
}

.bundle-chip-purple {
    color: #7d3cff;
    border-color: rgba(125, 60, 255, 0.25);
    background: rgba(125, 60, 255, 0.1);
}

.bundle-chip-default {
    color: #5f6a72;
    border-color: rgba(95, 106, 114, 0.25);
    background: rgba(95, 106, 114, 0.08);
}

.text-purple {
    color: #7d3cff;
}
</style>
